<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { tenant, websiteVariants, seoIndexationSitemap } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    websiteVariants: {
        type: Array,
        required: false,
    },
    seoIndexationSitemap: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const form = reactive({
    websiteVariant: websiteVariants.find(
        ({ id }) => id === seoIndexationSitemap?.websiteVariantId,
    ),
    name: seoIndexationSitemap ? seoIndexationSitemap.name : '',
    url: seoIndexationSitemap ? seoIndexationSitemap.url : '',
});

const validationSchema = object({
    websiteVariant: object().required('Ce champ est obligatoire.'),
    url: string().required('Ce champ est obligatoire.'),
    date: string().nullable(),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.seo.postIndexationSitemap', {
                tenant: tenant.id,
                ...(seoIndexationSitemap
                    ? { id: seoIndexationSitemap.id }
                    : {}),
            }),
            {
                websiteVariantId: form.websiteVariant.id,
                name: form.name,
                url: form.url,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.seo.indexation', {
                    tenant: tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <ComboboxField
                    filtered-option-key="name"
                    :options="websiteVariants"
                    field-name="websiteVariant"
                    field-id="websiteVariant"
                    label="Site"
                    :model-value="form.websiteVariant"
                    @update:modelValue="
                        (value) => (form.websiteVariant = value)
                    "
                    :required="true"
                >
                    <template #optionContent="{ option }">
                        {{ option.name }}
                    </template>
                </ComboboxField>

                <InputField
                    v-model="form.name"
                    label="Nom"
                    field-name="name"
                    field-id="name"
                    type="text"
                    required
                />

                <InputField
                    v-model="form.url"
                    label="URL"
                    field-name="url"
                    field-id="url"
                    type="url"
                    required
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="seoIndexationSitemap"
                    :url="
                        route(
                            'front.projectManagement.seo.deleteIndexationSitemap',
                            {
                                tenant: tenant.id,
                                id: seoIndexationSitemap.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.seo.indexation',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
